import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { PlusIcon, SearchIcon, TestIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { createLanguageTestTemplate } from '../../api/language-test/create-language-test-template'
import { getLanguageTestTemplates } from '../../api/language-test/get-language-test-templates'
import UserNavBar from '../../components/elements/nav-bar'
import LanguageTestCard from '../../components/pages/language-test/language-test-card'
import LanguageTestTemplateForm from '../../components/pages/language-test/language-test-template-form'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { useUrl } from '../../lib/hooks/use-url'
import useMemorizedDebounce from '../../lib/hooks/useMemorizedDebounce'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { queryClient } from '../../main'

const LagnuageTestList = () => {
  const { toggleModal, closeModel, modalState } = useModalManager<'language-test-template-form'>([
    'language-test-template-form'
  ])

  const { readSearchParam, setSearchParams } = useUrl()

  const [searchInput, setSearchInput] = useState(() => readSearchParam<string>('search') || '')
  const [search, setSearch] = useState(() => readSearchParam<string>('search') || '')

  const debouncedSearch = useMemorizedDebounce(searchInput, 500)

  const { data: languageTestTemplates, isLoading } = useQuery({
    queryKey: ['language-test-templates', search],
    queryFn: () => getLanguageTestTemplates({ search: search || undefined })
  })

  const { mutateAsync: createTemplate } = useMutation({
    mutationFn: createLanguageTestTemplate,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['language-test-templates'] })
      closeModel('language-test-template-form')
      toast.success('Test de langue créé avec succès')
    }
  })

  // Update search state and URL when debounced search changes
  useEffect(() => {
    setSearch(debouncedSearch)

    // Update URL with search parameter
    setSearchParams((params) => {
      const newParams = new URLSearchParams(params)
      if (debouncedSearch) newParams.set('search', debouncedSearch)
      else newParams.delete('search')
      return newParams.toString()
    })
  }, [debouncedSearch, setSearchParams])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
  }

  return (
    <div>
      <UserNavBar title={'Gestion des tests de langues'} icon={TestIcon} />
      <div className="h-4" />

      <Box>
        <div className="flex items-center justify-between mb-3">
          <Typography variant="sub-heading">Tests de langue</Typography>

          <Input
            options={{ clear: true }}
            type="text"
            icon={SearchIcon}
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
        <Button
          icon={PlusIcon}
          label="Créer un nouveau test"
          onClick={() => toggleModal('language-test-template-form')}
        />

        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="w-full h-36 rounded-lg" />
            ))}
          </div>
        ) : Array.isArray(languageTestTemplates) && languageTestTemplates.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {languageTestTemplates.map((template) => (
              <LanguageTestCard key={template.id} template={template} />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full border border-grey-light rounded-lg p-4 mt-4">
            <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
              {search
                ? 'Aucun test de langue trouvé pour cette recherche'
                : 'Aucun test de langue trouvé'}
            </Typography>
          </div>
        )}
      </Box>

      <Modal
        hideActions
        icon={TestIcon}
        title="Créer un nouveau test de langue"
        isOpen={modalState['language-test-template-form']}
        onCancel={() => closeModel('language-test-template-form')}
      >
        <LanguageTestTemplateForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('language-test-template-form'),
            onConfirm: createTemplate
          })}
        />
      </Modal>
    </div>
  )
}

export default LagnuageTestList
